<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
import { useTeamsStore } from '@/stores/teams'
import { useRadioLogStore } from '@/stores/radioLog'
import { useAssignmentsStore } from '@/stores/assignments'
import { MESSAGE_TYPES, MessageType, AssignmentStatus, PRIORITY_TRAFFIC_PLAN_LABELS } from '@/types'
import { useToastStore } from '@/stores/toast'
import type { Assignment } from '@/types'
import FosButton from '@/components/ui/FosButton.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'

const props = defineProps<{
  incidentId: string
  opId: string
}>()

const emit = defineEmits<{
  commsTransition: [assignmentId: string, status: AssignmentStatus]
}>()

const teamsStore = useTeamsStore()
const radioLogStore = useRadioLogStore()
const assignmentsStore = useAssignmentsStore()
const toast = useToastStore()

const teamInput = ref<string>('')
const messageTypeInput = ref<string>('')
const selectedAssignment = ref<Assignment | null>(null)
const assignmentInput = ref<string>('')
const priorityTrafficPlan = ref<string>('')
const notes = ref<string>('')
const submitting = ref(false)

const teamInputRef = ref<HTMLInputElement | null>(null)
const messageTypeInputRef = ref<HTMLInputElement | null>(null)
const assignmentInputRef = ref<HTMLInputElement | null>(null)
const notesRef = ref<InstanceType<typeof FosTextarea> | null>(null)

const teamSuggestions = ref<string[]>([])
const messageTypeSuggestions = ref<string[]>([])
const assignmentSuggestions = ref<Assignment[]>([])

const ASSIGNMENT_MESSAGE_TYPES: string[] = [
  MessageType.StartingAssignment,
  MessageType.CompletedAssignment,
]

const needsAssignment = computed(() =>
  ASSIGNMENT_MESSAGE_TYPES.includes(messageTypeInput.value),
)

const needsPriorityPlan = computed(() => messageTypeInput.value === MessageType.PriorityTraffic)

const matchedTeam = computed(() =>
  teamsStore.teams.find((t) => t.team_identifier === teamInput.value.trim()) ?? null
)

const teamAssignmentIds = computed(() => {
  if (!matchedTeam.value) return new Set<string>()
  return new Set(
    assignmentsStore.getTeamAssignments(matchedTeam.value.team_id).map((ta) => ta.assignment_id),
  )
})

const teamActiveAssignment = computed(() => {
  if (!matchedTeam.value) return null
  const ids = teamAssignmentIds.value
  return (
    assignmentsStore.assignments.find(
      (a) => ids.has(a.assignment_id) && a.assignment_status === AssignmentStatus.InProgress,
    ) ?? null
  )
})

watch(messageTypeInput, (newType) => {
  if (!ASSIGNMENT_MESSAGE_TYPES.includes(newType)) {
    selectedAssignment.value = null
    assignmentInput.value = ''
  }
  if (newType !== MessageType.PriorityTraffic) {
    priorityTrafficPlan.value = ''
  }
})

watch(assignmentInput, (val) => {
  if (selectedAssignment.value && val !== selectedAssignment.value.assignment_name) {
    selectedAssignment.value = null
  }
})

watch([messageTypeInput, matchedTeam], ([newType]) => {
  if (newType !== MessageType.CompletedAssignment) return
  if (selectedAssignment.value) return
  const active = teamActiveAssignment.value
  if (active) {
    selectedAssignment.value = active
    assignmentInput.value = active.assignment_name
  }
})

const teamIdentifiers = computed(() =>
  teamsStore.teams.map((t) => t.team_identifier),
)

function searchTeams(q: string) {
  const lower = q.toLowerCase()
  teamSuggestions.value = teamIdentifiers.value.filter((id) =>
    id.toLowerCase().includes(lower),
  )
}

function searchMessageTypes(q: string) {
  messageTypeSuggestions.value = q
    ? MESSAGE_TYPES.filter((t) => t.toLowerCase().startsWith(q.toLowerCase()))
    : [...MESSAGE_TYPES]
}

function searchAssignments(q: string) {
  const all = q
    ? assignmentsStore.assignments.filter((a) =>
        a.assignment_name.toLowerCase().includes(q.toLowerCase()),
      )
    : [...assignmentsStore.assignments]

  if (messageTypeInput.value === MessageType.StartingAssignment && matchedTeam.value) {
    const ids = teamAssignmentIds.value
    assignmentSuggestions.value = [
      ...all.filter((a) => ids.has(a.assignment_id)),
      ...all.filter((a) => !ids.has(a.assignment_id)),
    ]
  } else {
    assignmentSuggestions.value = all
  }
}

const assignmentGroupSplit = computed(() => {
  if (messageTypeInput.value !== MessageType.StartingAssignment || !matchedTeam.value) return 0
  const ids = teamAssignmentIds.value
  return assignmentSuggestions.value.filter((a) => ids.has(a.assignment_id)).length
})

const canSubmit = computed(() => {
  if (teamInput.value.trim() === '') return false
  if (!MESSAGE_TYPES.includes(messageTypeInput.value)) return false
  if (needsAssignment.value && assignmentInput.value.trim() === '') return false
  return true
})

async function submit() {
  if (!canSubmit.value || submitting.value) return

  submitting.value = true
  try {
    const matchedTeam = teamsStore.teams.find(
      (t) => t.team_identifier === teamInput.value.trim(),
    )
    await radioLogStore.addEntry(props.incidentId, props.opId, {
      team_identifier: teamInput.value.trim(),
      team_id: matchedTeam?.team_id ?? null,
      message_type: messageTypeInput.value,
      assignment_id: selectedAssignment.value?.assignment_id ?? null,
      assignment_name: (selectedAssignment.value?.assignment_name ?? assignmentInput.value.trim()) || null,
      priority_traffic_plan: priorityTrafficPlan.value || null,
      notes: notes.value.trim() || null,
    })

    const shouldTransition =
      messageTypeInput.value === MessageType.Enroute ||
      messageTypeInput.value === MessageType.RequestingAssignment ||
      (selectedAssignment.value &&
        (messageTypeInput.value === MessageType.StartingAssignment ||
          messageTypeInput.value === MessageType.CompletedAssignment))

    if (shouldTransition) {
      const transitionAssignmentId = selectedAssignment.value?.assignment_id ?? null
      const transitionType = messageTypeInput.value as MessageType
      try {
        await assignmentsStore.applyCommsTransition(
          props.incidentId,
          props.opId,
          transitionType,
          transitionAssignmentId,
          matchedTeam?.team_id ?? null,
        )
        if (transitionAssignmentId) {
          if (transitionType === MessageType.StartingAssignment) {
            emit('commsTransition', transitionAssignmentId, AssignmentStatus.InProgress)
          } else if (transitionType === MessageType.CompletedAssignment) {
            emit('commsTransition', transitionAssignmentId, AssignmentStatus.Completed)
          }
        }
      } catch (e) {
        toast.add(`Assignment status could not be updated: ${String(e)}`, 'warning', 6000)
      }
    }

    teamInput.value = ''
    messageTypeInput.value = ''
    selectedAssignment.value = null
    assignmentInput.value = ''
    priorityTrafficPlan.value = ''
    notes.value = ''
    teamSuggestions.value = []
    messageTypeSuggestions.value = []
    await nextTick()
    teamInputRef.value?.focus()
  } finally {
    submitting.value = false
  }
}

function focusMessageType() {
  nextTick(() => messageTypeInputRef.value?.focus())
}

function focusAssignment() {
  nextTick(() => assignmentInputRef.value?.focus())
}

function focusNotes() {
  nextTick(() => {
    const el = notesRef.value?.$el as HTMLTextAreaElement | undefined
    el?.focus()
  })
}

function onTeamKeydown(e: KeyboardEvent) {
  if (e.key === 'Tab' && teamSuggestions.value.length > 0) {
    e.preventDefault()
    teamInput.value = teamSuggestions.value[0]!
    teamSuggestions.value = []
    focusMessageType()
  } else if (e.key === 'Enter') {
    e.preventDefault()
    if (teamSuggestions.value.length > 0) {
      teamInput.value = teamSuggestions.value[0]!
      teamSuggestions.value = []
    }
    focusMessageType()
  } else if (e.key === 'Escape') {
    teamSuggestions.value = []
  }
}

function selectTeamSuggestion(val: string) {
  teamInput.value = val
  teamSuggestions.value = []
  focusMessageType()
}

function onMessageTypeKeydown(e: KeyboardEvent) {
  if (e.key === 'Tab' && messageTypeSuggestions.value.length > 0) {
    e.preventDefault()
    messageTypeInput.value = messageTypeSuggestions.value[0]!
    messageTypeSuggestions.value = []
    if (ASSIGNMENT_MESSAGE_TYPES.includes(messageTypeInput.value)) {
      focusAssignment()
    } else {
      focusNotes()
    }
  } else if (e.key === 'Escape') {
    messageTypeSuggestions.value = []
  }
}

function selectMessageTypeSuggestion(val: string) {
  messageTypeInput.value = val
  messageTypeSuggestions.value = []
  if (!ASSIGNMENT_MESSAGE_TYPES.includes(val)) {
    focusNotes()
  }
}

function onAssignmentKeydown(e: KeyboardEvent) {
  if (e.key === 'Tab' && assignmentSuggestions.value.length > 0) {
    e.preventDefault()
    selectAssignment(assignmentSuggestions.value[0]!)
  } else if (e.key === 'Escape') {
    assignmentSuggestions.value = []
  }
}

function selectAssignment(a: Assignment) {
  selectedAssignment.value = a
  assignmentInput.value = a.assignment_name
  assignmentSuggestions.value = []
  focusNotes()
}

function onNotesKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    submit()
  }
}

const inputClass = 'w-full bg-surface-bright text-on-surface font-mono text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary/30 placeholder:text-outline/50'
</script>

<template>
  <div class="bg-surface-container rounded p-4">
    <div
      class="grid grid-cols-1 gap-3 items-end"
      :class="needsAssignment || needsPriorityPlan
        ? 'sm:grid-cols-[1fr_1fr_1fr_2fr_auto]'
        : 'sm:grid-cols-[1fr_1fr_2fr_auto]'"
    >
      <!-- Team -->
      <div class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Team</label>
        <div class="relative">
          <input
            ref="teamInputRef"
            v-model="teamInput"
            :class="inputClass"
            placeholder="e.g. 101"
            autocomplete="off"
            @input="searchTeams(teamInput)"
            @focus="searchTeams(teamInput)"
            @blur="nextTick(() => { teamSuggestions = [] })"
            @keydown="onTeamKeydown"
          />
          <div
            v-if="teamSuggestions.length > 0"
            class="absolute z-50 top-full left-0 right-0 bg-surface-container-high border-t border-border shadow-xl max-h-40 overflow-y-auto"
          >
            <button
              v-for="s in teamSuggestions"
              :key="s"
              type="button"
              class="w-full text-left px-3 py-2 font-mono text-sm text-on-surface hover:bg-surface-container-highest transition-colors"
              @mousedown.prevent="selectTeamSuggestion(s)"
            >
              {{ s }}
            </button>
          </div>
        </div>
      </div>

      <!-- Message Type -->
      <div class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Message</label>
        <div class="relative">
          <input
            ref="messageTypeInputRef"
            v-model="messageTypeInput"
            :class="inputClass"
            placeholder="Type to search..."
            autocomplete="off"
            @input="searchMessageTypes(messageTypeInput)"
            @focus="searchMessageTypes(messageTypeInput)"
            @blur="nextTick(() => { messageTypeSuggestions = [] })"
            @keydown="onMessageTypeKeydown"
          />
          <div
            v-if="messageTypeSuggestions.length > 0"
            class="absolute z-50 top-full left-0 right-0 bg-surface-container-high border-t border-border shadow-xl max-h-48 overflow-y-auto"
          >
            <button
              v-for="s in messageTypeSuggestions"
              :key="s"
              type="button"
              class="w-full text-left px-3 py-2 font-mono text-sm text-on-surface hover:bg-surface-container-highest transition-colors"
              @mousedown.prevent="selectMessageTypeSuggestion(s)"
            >
              {{ s }}
            </button>
          </div>
        </div>
      </div>

      <!-- Assignment (conditional) -->
      <div v-if="needsAssignment" class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Assignment</label>
        <div class="relative">
          <input
            ref="assignmentInputRef"
            v-model="assignmentInput"
            :class="inputClass"
            placeholder="Search or type..."
            autocomplete="off"
            @input="searchAssignments(assignmentInput)"
            @focus="searchAssignments(assignmentInput)"
            @blur="nextTick(() => { assignmentSuggestions = [] })"
            @keydown="onAssignmentKeydown"
          />
          <div
            v-if="assignmentSuggestions.length > 0"
            class="absolute z-50 top-full left-0 right-0 bg-surface-container-high border-t border-border shadow-xl max-h-48 overflow-y-auto"
          >
            <template v-for="(a, i) in assignmentSuggestions" :key="a.assignment_id">
              <div
                v-if="i === 0 && assignmentGroupSplit > 0"
                class="px-3 py-1 font-mono text-[0.6rem] uppercase tracking-widest text-outline bg-surface-container-lowest/60 border-b border-border"
              >
                Assigned
              </div>
              <div
                v-if="assignmentGroupSplit > 0 && i === assignmentGroupSplit"
                class="px-3 py-1 font-mono text-[0.6rem] uppercase tracking-widest text-outline bg-surface-container-lowest/60 border-y border-border"
              >
                Other
              </div>
              <button
                type="button"
                class="w-full text-left px-3 py-2 font-mono text-sm text-on-surface hover:bg-surface-container-highest transition-colors"
                @mousedown.prevent="selectAssignment(a)"
              >
                {{ a.assignment_name }}
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- Priority Traffic Plan (conditional) -->
      <div v-if="needsPriorityPlan" class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Plan</label>
        <div class="flex gap-1">
          <button
            v-for="(label, plan) in PRIORITY_TRAFFIC_PLAN_LABELS"
            :key="plan"
            type="button"
            :class="[
              'flex-1 font-mono text-sm px-3 py-2 border transition-colors focus:outline-none',
              priorityTrafficPlan === plan
                ? 'bg-primary-container text-white border-primary-container'
                : 'bg-surface-container-high text-on-surface border-border hover:bg-surface-container-highest',
            ]"
            :title="label"
            @click="priorityTrafficPlan = plan"
          >
            {{ plan }}
          </button>
        </div>
      </div>

      <!-- Notes -->
      <div class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">
          Notes <span class="font-normal text-outline/50 normal-case tracking-normal">(optional)</span>
        </label>
        <FosTextarea
          ref="notesRef"
          v-model="notes"
          :class="inputClass"
          placeholder="Optional notes..."
          :rows="1"
          @keydown="onNotesKeydown"
        />
      </div>

      <!-- Submit -->
      <FosButton
        :disabled="!canSubmit || submitting"
        @click="submit"
      >
        {{ submitting ? 'Logging...' : 'Log' }}
      </FosButton>
    </div>
  </div>
</template>
